/**
 * Remote Cursor - CodeMirror 6 遠端游標裝飾擴展
 *
 * 顯示其他協作者的游標位置和名稱標籤
 *
 * @module @linkode/web/editor/cm/remoteCursor
 * @see SPEC-007 Requirements 6.2
 */

import { Extension, StateField, StateEffect, RangeSet } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView, WidgetType } from '@codemirror/view';
import type { Position } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Remote cursor data for a single participant
 */
export interface RemoteCursorData {
  /** Unique user ID */
  userId: string;
  /** Cursor position (line/column, 0-indexed) */
  cursor: Position | null;
  /** User's display name */
  displayName: string;
  /** Color ID (0-7) for visual distinction */
  colorId: number;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Presence color palette (8 colors, matching colorId 0-7)
 * @see SPEC-007 Requirement 4.10
 */
export const PRESENCE_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
] as const;

// ============================================================================
// Position Conversion Utilities
// ============================================================================

/**
 * Convert CodeMirror offset to line/column Position
 *
 * @param view - EditorView instance
 * @param offset - Character offset in document
 * @returns Position with line and column (0-indexed)
 */
export function offsetToPosition(view: EditorView, offset: number): Position {
  const line = view.state.doc.lineAt(offset);
  return {
    line: line.number - 1, // CodeMirror lines are 1-indexed, convert to 0-indexed
    column: offset - line.from,
  };
}

/**
 * Convert line/column Position to CodeMirror offset
 *
 * @param view - EditorView instance
 * @param position - Position with line and column (0-indexed)
 * @returns Character offset in document, or null if position is invalid
 */
export function positionToOffset(view: EditorView, position: Position): number | null {
  const { line, column } = position;
  const lineCount = view.state.doc.lines;

  // Validate line number (0-indexed input, but doc.line expects 1-indexed)
  if (line < 0 || line >= lineCount) {
    return null;
  }

  const lineInfo = view.state.doc.line(line + 1); // Convert to 1-indexed

  // Clamp column to line length
  const clampedColumn = Math.min(Math.max(0, column), lineInfo.length);

  return lineInfo.from + clampedColumn;
}

// ============================================================================
// Remote Cursor Widget
// ============================================================================

/**
 * Widget that renders a remote user's cursor with name label
 */
class RemoteCursorWidget extends WidgetType {
  constructor(
    readonly userId: string,
    readonly displayName: string,
    readonly colorId: number
  ) {
    super();
  }

  eq(other: RemoteCursorWidget): boolean {
    return (
      other.userId === this.userId &&
      other.displayName === this.displayName &&
      other.colorId === this.colorId
    );
  }

  toDOM(): HTMLElement {
    const color = PRESENCE_COLORS[this.colorId % PRESENCE_COLORS.length];

    // Container for cursor and label
    const container = document.createElement('span');
    container.className = 'cm-remote-cursor';
    container.setAttribute('data-user-id', this.userId);
    container.style.cssText = `
      position: relative;
      display: inline-block;
      width: 0;
      height: 100%;
      pointer-events: none;
    `;

    // Cursor line (vertical bar)
    const cursorLine = document.createElement('span');
    cursorLine.className = 'cm-remote-cursor-line';
    cursorLine.style.cssText = `
      position: absolute;
      left: 0;
      top: 0;
      width: 2px;
      height: 1.2em;
      background-color: ${color};
      border-radius: 1px;
      animation: cm-cursor-blink 1s ease-in-out infinite;
    `;

    // Name label (shown above cursor)
    const label = document.createElement('span');
    label.className = 'cm-remote-cursor-label';
    label.textContent = this.displayName;
    label.style.cssText = `
      position: absolute;
      left: 0;
      bottom: 100%;
      margin-bottom: 2px;
      padding: 2px 6px;
      font-size: 10px;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
      color: white;
      background-color: ${color};
      border-radius: 3px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      opacity: 0.9;
      z-index: 10;
    `;

    container.appendChild(cursorLine);
    container.appendChild(label);

    return container;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

// ============================================================================
// State Effects and Field
// ============================================================================

/**
 * Effect to update remote cursors
 */
export const setRemoteCursors = StateEffect.define<RemoteCursorData[]>();

/**
 * State field that tracks remote cursor decorations
 */
const remoteCursorField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },

  update(decorations, tr) {
    // Check for cursor update effects
    for (const effect of tr.effects) {
      if (effect.is(setRemoteCursors)) {
        return buildCursorDecorations(tr.state.doc, effect.value);
      }
    }

    // Map decorations through document changes
    if (tr.docChanged) {
      decorations = decorations.map(tr.changes);
    }

    return decorations;
  },

  provide: (field) => EditorView.decorations.from(field),
});

/**
 * Build decoration set from remote cursor data
 */
function buildCursorDecorations(
  doc: { lines: number; line: (n: number) => { from: number; length: number } },
  cursors: RemoteCursorData[]
): DecorationSet {
  const decorations: { from: number; decoration: Decoration }[] = [];

  for (const cursor of cursors) {
    if (!cursor.cursor) continue;

    const { line, column } = cursor.cursor;
    const lineCount = doc.lines;

    // Validate line number
    if (line < 0 || line >= lineCount) continue;

    const lineInfo = doc.line(line + 1); // Convert to 1-indexed
    const clampedColumn = Math.min(Math.max(0, column), lineInfo.length);
    const offset = lineInfo.from + clampedColumn;

    decorations.push({
      from: offset,
      decoration: Decoration.widget({
        widget: new RemoteCursorWidget(cursor.userId, cursor.displayName, cursor.colorId),
        side: 1, // Place after the character
      }),
    });
  }

  // Sort by position
  decorations.sort((a, b) => a.from - b.from);

  return RangeSet.of(decorations.map((d) => d.decoration.range(d.from)));
}

// ============================================================================
// Styles
// ============================================================================

/**
 * Remote cursor styles including animation
 */
const remoteCursorStyles = EditorView.baseTheme({
  '.cm-remote-cursor': {
    position: 'relative',
  },
  '@keyframes cm-cursor-blink': {
    '0%, 100%': { opacity: '1' },
    '50%': { opacity: '0.5' },
  },
});

// ============================================================================
// Extension Factory
// ============================================================================

/**
 * Create Remote Cursor extension for CodeMirror
 *
 * @returns CodeMirror extension for remote cursors
 *
 * @example
 * ```typescript
 * import { createRemoteCursorExtension, setRemoteCursors } from './remoteCursor';
 *
 * // Add extension to editor
 * const extensions = [
 *   createRemoteCursorExtension(),
 *   // ... other extensions
 * ];
 *
 * // Update remote cursors
 * view.dispatch({
 *   effects: setRemoteCursors.of([
 *     { userId: 'user1', cursor: { line: 0, column: 5 }, displayName: 'Alice', colorId: 0 },
 *     { userId: 'user2', cursor: { line: 2, column: 10 }, displayName: 'Bob', colorId: 1 },
 *   ])
 * });
 * ```
 */
export function createRemoteCursorExtension(): Extension {
  return [remoteCursorField, remoteCursorStyles];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Update remote cursors in an EditorView
 *
 * @param view - EditorView instance
 * @param cursors - Array of remote cursor data
 */
export function updateRemoteCursors(view: EditorView, cursors: RemoteCursorData[]): void {
  view.dispatch({
    effects: setRemoteCursors.of(cursors),
  });
}

export default createRemoteCursorExtension;
